import { DatePicker, Flex, Select } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  REPORT_WINDOW_PRESETS,
  type ReportWindow,
  type ReportWindowPreset,
  isCustomWindow
} from "../../types/canvas.types.ts"

const DEFAULT_PRESET: ReportWindowPreset = "this_month"
const CUSTOM = "custom"

interface ReportDateRangeControlProps {
  window: ReportWindow | undefined
  onChange: (window: ReportWindow) => void
  disabled?: boolean
}

export const ReportDateRangeControl = ({
  window,
  onChange,
  disabled = false
}: ReportDateRangeControlProps) => {
  const { t } = useTranslation()

  const custom = isCustomWindow(window)
  const currentValue: ReportWindowPreset | typeof CUSTOM = custom
    ? CUSTOM
    : (window?.preset ?? DEFAULT_PRESET)

  const [selected, setSelected] = useState<ReportWindowPreset | typeof CUSTOM>(
    currentValue
  )

  useEffect(() => {
    setSelected(currentValue)
  }, [currentValue])

  const options = [
    ...REPORT_WINDOW_PRESETS.map((preset) => ({
      label: t(`reports.window.${preset}`),
      value: preset
    })),
    { label: t("reports.window.custom"), value: CUSTOM }
  ]

  const handleSelect = (value: ReportWindowPreset | typeof CUSTOM) => {
    setSelected(value)
    if (value !== CUSTOM) onChange({ preset: value })
  }

  const handleRange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      setSelected(DEFAULT_PRESET)
      onChange({ preset: DEFAULT_PRESET })
      return
    }
    onChange({
      from: dates[0].format("YYYY-MM-DD"),
      to: dates[1].format("YYYY-MM-DD")
    })
  }

  const rangeValue: [Dayjs, Dayjs] | undefined =
    window && isCustomWindow(window)
      ? [dayjs(window.from), dayjs(window.to)]
      : undefined

  return (
    <Flex gap="small" align="center" wrap="wrap">
      <Select<ReportWindowPreset | typeof CUSTOM>
        aria-label={t("reports.window.label")}
        value={selected}
        options={options}
        onChange={handleSelect}
        disabled={disabled}
        // A short menu; virtual scrolling only hides its tail from a test.
        virtual={false}
        style={{ minWidth: 150 }}
      />
      {selected === CUSTOM && (
        <DatePicker.RangePicker
          value={rangeValue}
          onChange={handleRange}
          disabled={disabled}
        />
      )}
    </Flex>
  )
}
